import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Required } from 'utility-types';

import { CategoryType } from '@core/shared/domain';
import {
  ArticleMatchFilter,
  ArticleSearchRequest,
  ArticleTypeFilter,
} from '@mp/content-manager/artikelsuche/data-access';
import {
  Comparators,
  UrlFilterFacade,
  UrlFilterFacadeConfig,
  getEnumValue,
  getEnumValues,
} from '@mp/shared/data-access';
import { ArticleSearchQueryField } from '@mp/shared/domain';
import { FacetDrilldown } from '@mp/shared/facets/domain';
import { AdditionalArticleStatus, ArticleStatus } from '@mpcm/shared';

import { FilterRequiredFields, FilterType } from './models';

const requiredDefaults: Required<Pick<ArticleSearchRequest, FilterRequiredFields>> = {
  page: 1,
  pageSize: 50,
  articleType: ArticleTypeFilter.All,
  articleMatch: ArticleMatchFilter.All,
  field: ArticleSearchQueryField.All,
};

const filterStateComparator = Comparators.ForType<FilterType>({
  page: Comparators.ByValue,
  pageSize: Comparators.ByValue,

  query: Comparators.ByValue,
  field: Comparators.ByValue,
  suppliers: Comparators.PrimitiveArray,
  manufacturers: Comparators.PrimitiveArray,
  eClassFeatures: Comparators.PrimitiveArray,
  articleFeatures: Comparators.PrimitiveArray,
  pharmaceuticalFeatures: Comparators.PrimitiveArray,
  category: Comparators.ByValue,
  categoryType: Comparators.ByValue ?? undefined,
  articleType: Comparators.ByValue,
  articleMatch: Comparators.ByValue,
  articleRanges: Comparators.PrimitiveArray,
  searchViewId: Comparators.ByValue ?? undefined,

  expandLimit: Comparators.ByValue,
  minCount: Comparators.ByValue,
  articleFeatureDrilldown: Comparators.PrimitiveArray,
  eClassFeatureDrilldown: Comparators.PrimitiveArray,
  pharmaceuticalFeatureDrilldown: Comparators.PrimitiveArray,
  currentArticleStatus: Comparators.ByValue,
  currentArticleStatusDate: Comparators.ByValue,
  additionalArticleStatus: Comparators.ByValue,
  additionalArticleStatusDate: Comparators.ByValue,
  futureArticleStatus: Comparators.ByValue,
  futureArticleStatusDate: Comparators.ByValue,
});

@Injectable()
export class ArtikelsucheFilterFacade extends UrlFilterFacade<FilterType> implements OnDestroy {
  constructor(route: ActivatedRoute, router: Router) {
    const filterFacadeConfig: UrlFilterFacadeConfig<FilterType> = {
      routing: { route, router },
      autoWritebackToUrl: false,
      filterStateComparator,
    };

    super(filterFacadeConfig);
  }

  // eslint-disable-next-line complexity
  protected override readFilterStateFromURL(paramMap: ParamMap): FilterType {
    function getNumberValue(key: string): number | undefined {
      const value = paramMap.get(key);
      if (value != null) {
        const num = Number(value);
        if (!isNaN(num)) {
          return num;
        }
      }

      return undefined;
    }

    return {
      page: getNumberValue('page') ?? requiredDefaults.page,
      pageSize: getNumberValue('pageSize') ?? requiredDefaults.pageSize,

      query: paramMap.get('query') ?? undefined,
      field: (paramMap.get('field') ?? ArticleSearchQueryField.All) as ArticleSearchQueryField,
      suppliers: paramMap.getAll('suppliers'),
      manufacturers: paramMap.getAll('manufacturers'),
      eClassFeatures: paramMap.getAll('eClassFeatures'),
      articleFeatures: paramMap.getAll('articleFeatures'),
      pharmaceuticalFeatures: paramMap.getAll('pharmaceuticalFeatures'),
      category: paramMap.get('category') ?? undefined,
      categoryType: getEnumValue(CategoryType, paramMap.get('categoryType')),
      articleType: getEnumValue(ArticleTypeFilter, paramMap.get('articleType')) ?? requiredDefaults.articleType,
      articleMatch: getEnumValue(ArticleMatchFilter, paramMap.get('articleMatch')) ?? requiredDefaults.articleMatch,
      articleRanges: paramMap.getAll('articleRanges'),
      currentArticleStatus: getEnumValues(ArticleStatus, paramMap.getAll('currentArticleStatus')),
      currentArticleStatusDate: paramMap.get('currentArticleStatusDate') ?? undefined,
      additionalArticleStatus: getEnumValues(AdditionalArticleStatus, paramMap.getAll('additionalArticleStatus')),
      additionalArticleStatusDate: paramMap.get('additionalArticleStatusDate') ?? undefined,
      futureArticleStatus: getEnumValues(ArticleStatus, paramMap.getAll('futureArticleStatus')),
      futureArticleStatusDate: paramMap.get('futureArticleStatusDate') ?? undefined,
      searchViewId: paramMap.get('searchViewId') ?? undefined,

      expandLimit: getNumberValue('expandLimit'),
      minCount: getNumberValue('minCount'),
      articleFeatureDrilldown: paramMap.getAll('articleFeatureDrilldown') as Array<FacetDrilldown> | undefined,
      eClassFeatureDrilldown: paramMap.getAll('eClassFeatureDrilldown') as Array<FacetDrilldown> | undefined,
      pharmaceuticalFeatureDrilldown: paramMap.getAll('pharmaceuticalFeatureDrilldown') as
        | Array<FacetDrilldown>
        | undefined,
    };
  }

  public isPageChangeOnly(a: FilterType, b: FilterType): boolean {
    // TODO: Geht das besser, als nochmal den Vergleich zu fahren? Evtl. zwei Url-Listener?
    if (
      (a.page ?? requiredDefaults.page) === (b.page ?? requiredDefaults.page) &&
      (a.pageSize ?? requiredDefaults.pageSize) === (b.pageSize ?? requiredDefaults.pageSize)
    ) {
      return false;
    }

    return filterStateComparator(
      {
        ...a,
        page: 1,
        pageSize: 1,
      },
      {
        ...b,
        page: 1,
        pageSize: 1,
      },
    );
  }

  protected override writeFilterStateToURL(filter: FilterType, router: Router, route: ActivatedRoute): void {
    function filterDefaultAnyEmptyValues(filter: FilterType) {
      return Object.entries(filter)
        .filter(
          ([key, value]) =>
            value != null &&
            !(
              key in requiredDefaults &&
              Comparators.ByValue(requiredDefaults[key as keyof typeof requiredDefaults], value)
            ),
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    }

    const queryParams = filterDefaultAnyEmptyValues(filter);

    router.navigate([], { relativeTo: route, replaceUrl: true, queryParams });
  }

  updateFilterAndResetPage(partial: Partial<FilterType>): void {
    const newFilter: FilterType = {
      ...(this.filter ?? {}),
      ...partial,
    };
    // Direkt beim Laden kommt ein filterchanged aus der Filter-Komponente (mehh...).
    // Daher hier mal prüfen, ob sich wirklich was ändern. Sonst nicht die page resetten.
    if (this.filter && filterStateComparator(this.filter, newFilter)) {
      return;
    }

    newFilter.page = 1;
    this.setFilter(newFilter);
  }

  resetFilter(): void {
    this.setFilter({
      ...requiredDefaults,
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeURLListener();
  }
}
